import Tooltip from '../ui-control/Tooltip';
import { DemandStudioProvider } from '../../contexts/DemandStudioContext';
import DemandStudioHeaderButton from '../demand-studio/DemandStudioHeaderButton';
import DemandStudioPreviewDownloadButton from '../demand-studio/DemandStudioPreviewDownloadButton';
import DemandStudioExitOverlay from '../demand-studio/DemandStudioExitOverlay';
import DemandStudioTipsPopover from '../demand-studio/DemandStudioTipsPopover';
import UndoRedoButtons from '../demand-studio/UndoRedoButtons';
import getPreviewButtonState from '../demand-studio/previewButtonState';
import LifecareStudioAutosaveBadge from './LifecareStudioAutosaveBadge';
import LifecareStudioBody from './LifecareStudioBody';
import LifecareStudioChronologyDownloadButton from './LifecareStudioChronologyDownloadButton';
import LifecareStudioClientSelector from './LifecareStudioClientSelector';
import LifecareStudioPreviewUpdatingBadge from './LifecareStudioPreviewUpdatingBadge';
import LifecareStudioApproveButton from './LifecareStudioApproveButton';

const fullscreenClasses = 'flex flex-col h-[calc(100vh-56px)] overflow-hidden';

const headerClasses =
  'flex flex-col gap-2 py-6 px-10 border-b border-b-border ' +
  'shrink-0 bg-white shadow-[0_4px_4px_rgba(0,0,0,0.15)]';

const headerRowClasses = 'flex items-center justify-between w-full';

const headerLeftClasses = 'flex flex-1 items-center gap-4 min-w-0';

const headerRightClasses = 'inline-flex items-center gap-4 ml-6';

const headerActionsClasses = 'relative inline-flex items-center gap-2';

const headerTitleClasses =
  'text-section-title text-content font-serif ' +
  'm-0! max-w-[40vw] whitespace-nowrap overflow-hidden text-ellipsis';

const headerIconBtnClasses =
  'inline-flex items-center justify-center h-10 w-10 border border-border ' +
  'rounded-full bg-white font-secondary text-base text-subtle cursor-pointer ' +
  'transition-[border-color,color,background-color] duration-150 ' +
  'hover:not-disabled:border-primary hover:not-disabled:text-primary ' +
  'hover:not-disabled:bg-primary/[0.08] ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

const headerCloseClasses =
  'flex items-center gap-2 font-secondary font-semibold text-base ' +
  'text-content cursor-pointer hover:text-primary';

const bodyClasses = 'flex-1 overflow-hidden p-6 bg-surface-card min-h-0';

const LifecareStudioLayout = ({ studioState }) => {
  const {
    caseName,
    isInitialLoadPending,
    showPreview,
    isPreviewLoading,
    isPreviewStale,
    hasAnyLifecareClient,
    isNewPlanForSelectedClient,
    lifecareClientIdAndDisplayNames,
    selectedClientId,
    setSelectedClientId,
    autosaveStatus,
    formState,
    previewState,
    suggestedEditsState,
    undoState,
    tabState,
    tips,
    approveState,
    isFinalizingExit,
    handleSaveAndClose,
    handleTogglePreview,
    handlePreview,
  } = studioState;

  const { isOpen: isTipsOpen, anchorRef: tipsAnchorRef, toggleTips, closeTips } = tips;

  // block preview while a suggested edit is running so it never regenerates from a pre-edit snapshot
  const isSuggestedEditRunning = Boolean(suggestedEditsState?.isAnySectionRegenerating);
  const { isPreviewButtonLoading, previewTooltip } = getPreviewButtonState({
    isPreviewLoading,
    isSuggestedEditRunning,
  });

  return (
    <DemandStudioProvider value={studioState}>
    <div className={fullscreenClasses}>
      <div className={headerClasses}>
        <div className={headerRowClasses}>
          <div className={headerLeftClasses}>
            <h1 className={headerTitleClasses}>{caseName}</h1>
            <LifecareStudioClientSelector
              lifecareClientIdAndDisplayNames={lifecareClientIdAndDisplayNames}
              selectedClientId={selectedClientId}
              setSelectedClientId={setSelectedClientId}
            />
            {showPreview && isPreviewLoading && <LifecareStudioPreviewUpdatingBadge />}
            {showPreview && <LifecareStudioApproveButton {...approveState} />}
            {!showPreview && <LifecareStudioAutosaveBadge status={autosaveStatus} />}
          </div>
          <div className={headerRightClasses}>
            <div className={headerActionsClasses} ref={tipsAnchorRef}>
              {!showPreview && (
                <UndoRedoButtons
                  isUndoLoading={undoState.isUndoLoading}
                  isSuggestedEditRunning={isSuggestedEditRunning}
                  isManualEditProcessing={undoState.isManualEditProcessing}
                  hasUndo={undoState.hasUndo}
                  hasRedo={undoState.hasRedo}
                  handleUndo={undoState.handleUndo}
                  variant="iconHeader"
                  className={headerIconBtnClasses}
                />
              )}
              <LifecareStudioChronologyDownloadButton className={headerIconBtnClasses} />
              <Tooltip title="Tips" placement="bottom" leaveDelay={50}>
                <DemandStudioHeaderButton
                  icon="fa-regular fa-lightbulb"
                  label=""
                  ariaLabel="Tips"
                  onClick={toggleTips}
                  isActive={isTipsOpen}
                  className={headerIconBtnClasses}
                  activeClassName="border-primary! text-primary! bg-primary/[0.08]!"
                />
              </Tooltip>
              <DemandStudioTipsPopover isOpen={isTipsOpen} onClose={closeTips} />
              {showPreview ? (
                <Tooltip title="Close Preview" placement="bottom" leaveDelay={50}>
                  <DemandStudioHeaderButton
                    icon="fa fa-eye-slash"
                    label=""
                    ariaLabel="Close Preview"
                    onClick={handleTogglePreview}
                    className={headerIconBtnClasses}
                  />
                </Tooltip>
              ) : (
                <Tooltip
                  title={previewTooltip}
                  placement="bottom"
                  leaveDelay={50}
                >
                  <DemandStudioHeaderButton
                    icon={isPreviewButtonLoading ? 'fa fa-spinner animate-spin' : 'fa fa-eye'}
                    label=""
                    ariaLabel={previewTooltip}
                    onClick={handlePreview}
                    disabled={isPreviewButtonLoading || !hasAnyLifecareClient}
                    className={headerIconBtnClasses}
                  />
                </Tooltip>
              )}
              {showPreview && <DemandStudioPreviewDownloadButton previewState={previewState} />}
            </div>
            <div className={headerCloseClasses} onClick={handleSaveAndClose}>
              <span>Save and Close</span>
              <i className="fa fa-times" />
            </div>
          </div>
        </div>
      </div>
      <div className={bodyClasses}>
        <LifecareStudioBody
          isInitialLoadPending={isInitialLoadPending}
          hasAnyLifecareClient={hasAnyLifecareClient}
          showPreview={showPreview}
          isPreviewStale={isPreviewStale}
          isNewPlanForSelectedClient={isNewPlanForSelectedClient}
          formState={formState}
          previewState={previewState}
          suggestedEditsState={suggestedEditsState}
          tabState={tabState}
        />
      </div>
      <DemandStudioExitOverlay isVisible={isFinalizingExit} />
    </div>
    </DemandStudioProvider>
  );
};

export default LifecareStudioLayout;
